import './Settings.scss';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { setNovodroidToken, setNovodroidUrl } from '../../storeRedux';
import CopyButton from '../../components/CopyButton/CopyButton';
import Modal from 'react-modal';

Modal.setAppElement('#root');

export default function Settings() {

    const { t } = useTranslation('settings');
    const theme = useSelector((state) => state.APP_THEME);
    const userRole = useSelector((state) => state.USER_ROLE);
    const user_code = useSelector((state) => state.USER_CODE);
    const user_chat = useSelector((state) => state.USER_CHAT);
    const bot_notifier = useSelector((state) => state.BOT_NOTIFIER);
    const bot_notifier_error = useSelector((state) => state.BOT_NOTIFIER_ERROR);
    const support_bot = useSelector((state) => state.SUPPORT_BOT);
    const support_bot_link = useSelector((state) => state.SUPPORT_BOT_LINK);
    const support_bot_connected = useSelector((state) => state.SUPPORT_BOT_CONNECTED);
    const support_bot_topic_active = useSelector((state) => state.SUPPORT_BOT_TOPIC_ACTIVE);
    const support_bot_mode = useSelector((state) => state.SUPPORT_BOT_MODE);

    const novodroid_token = useSelector((state) => state.NOVODROID_TOKEN);
    const novodroid_url = useSelector((state) => state.NOVODROID_URL);
    const api_url = useSelector((state) => state.API_URL);

    const dispatch = useDispatch();

    const [regenerating, setRegenerating] = useState(false);
    const [showRegenerateModal, setShowRegenerateModal] = useState(false);
    const [apkAvailable, setApkAvailable] = useState(false);

    const [receiptSettings, setReceiptSettings] = useState(null);
    const [receiptBotToken, setReceiptBotToken] = useState('');
    const [receiptChannelId, setReceiptChannelId] = useState('');
    const [receiptPdfToJpg, setReceiptPdfToJpg] = useState(false);
    const [receiptGroups, setReceiptGroups] = useState([]);
    const [receiptBusy, setReceiptBusy] = useState('');
    const [receiptMessage, setReceiptMessage] = useState(null);

    // Teamlead profile data
    const [tlProfile, setTlProfile] = useState(null);

    const navigate = useNavigate();

    const handleRegenerateToken = () => {
        setShowRegenerateModal(false);
        const jwtToken = localStorage.getItem('accessToken');
        setRegenerating(true);
        fetch(api_url + 'novodroid/token/', {
            method: 'POST',
            headers: {
                'Authorization': `JWT ${jwtToken}`,
                'Content-Type': 'application/json'
            }
        })
        .then(response => response.json())
        .then(data => {
            if (data.novodroid_token) {
                dispatch(setNovodroidToken(data.novodroid_token));
            }
            if (data.novodroid_url) {
                dispatch(setNovodroidUrl(data.novodroid_url));
            }
            setRegenerating(false);
        })
        .catch(() => setRegenerating(false));
    };

    useEffect(() => {
        const jwtToken = localStorage.getItem('accessToken');
        if (!jwtToken) {
            navigate('/auth');
            localStorage.clear();
            return;
        }

        // Загружаем профиль тимлидера
        if (userRole === 'teamlead') {
            fetch(api_url + 'teamlead/profile/', {
                headers: { 'Authorization': `JWT ${jwtToken}`, 'Content-Type': 'application/json' },
            })
            .then(r => r.ok ? r.json() : null)
            .then(data => { if (data) setTlProfile(data); })
            .catch(() => {});
        }

        if (userRole === 'operator') {
            fetch(api_url + 'trader/receipt-settings/', {
                headers: { 'Authorization': `JWT ${jwtToken}` },
            })
            .then(async r => {
                const data = await r.json();
                if (!r.ok) throw new Error(data.error || t('settings:receiptLoadError'));
                return data;
            })
            .then(data => {
                setReceiptSettings(data);
                setReceiptChannelId(data.channel_id || '');
                setReceiptPdfToJpg(!!data.pdf_to_jpg);
            })
            .catch(error => setReceiptMessage({ type: 'error', text: error.message }));
        }

        fetch(api_url + 'apk/status/', {
            headers: { 'Authorization': `JWT ${jwtToken}` }
        })
        .then(r => r.json())
        .then(data => setApkAvailable(data.available))
        .catch(() => {});
    }, [navigate, api_url, userRole, t]);

    const receiptRequest = async (method, body, busyKind) => {
        const jwtToken = localStorage.getItem('accessToken');
        setReceiptBusy(busyKind);
        setReceiptMessage(null);
        try {
            const response = await fetch(api_url + 'trader/receipt-settings/', {
                method,
                headers: {
                    'Authorization': `JWT ${jwtToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(body),
            });
            const data = await response.json();
            if (!response.ok) {
                const requestError = new Error(data.error || t('settings:receiptRequestError'));
                requestError.hint = data.hint;
                requestError.field = data.field;
                requestError.code = data.code;
                throw requestError;
            }
            return data;
        } finally {
            setReceiptBusy('');
        }
    };

    const saveReceiptSettings = async () => {
        try {
            const data = await receiptRequest('PUT', {
                bot_token: receiptBotToken,
                channel_id: receiptChannelId,
                pdf_to_jpg: receiptPdfToJpg,
            }, 'save');
            setReceiptSettings(data);
            setReceiptBotToken('');
            setReceiptMessage({ type: 'success', text: data.message });
        } catch (error) {
            setReceiptMessage({ type: 'error', text: error.message, hint: error.hint, field: error.field });
        }
    };

    const checkReceiptSettings = async () => {
        try {
            const data = await receiptRequest('POST', {
                action: 'check', bot_token: receiptBotToken, channel_id: receiptChannelId,
            }, 'check');
            setReceiptMessage({ type: 'success', text: data.message });
        } catch (error) {
            setReceiptMessage({ type: 'error', text: error.message, hint: error.hint, field: error.field });
        }
    };

    const detectReceiptGroups = async () => {
        try {
            const data = await receiptRequest('POST', {
                action: 'detect', bot_token: receiptBotToken,
            }, 'detect');
            setReceiptGroups(data.groups || []);
            setReceiptMessage({
                type: data.groups?.length ? 'success' : 'warning',
                text: data.groups?.length
                    ? t('settings:receiptGroupsFound', { count: data.groups.length })
                    : t('settings:receiptGroupsNotFound'),
            });
        } catch (error) {
            setReceiptMessage({ type: 'error', text: error.message, hint: error.hint, field: error.field });
        }
    };

    const notifierLink = bot_notifier ? `https://t.me/${bot_notifier}?start=code${user_code}` : '';
    const telegramAccount = user_chat
        ? (typeof user_chat === 'object' ? user_chat : { display_name: user_chat })
        : null;
    const telegramName = telegramAccount?.display_name
        || [telegramAccount?.first_name, telegramAccount?.last_name].filter(Boolean).join(' ');

    const supportLink = support_bot_mode === 'with_token'
        ? support_bot_link
        : (support_bot_link || (support_bot ? `https://t.me/${support_bot}` : ''));

    const getSupportStatus = () => {
        if (support_bot_connected) {
            if (support_bot_topic_active) {
                return { label: t('settings:connected'), type: 'success' };
            }
            return { label: t('settings:chatInactive'), type: 'warning' };
        }
        return { label: t('settings:notConnected'), type: 'warning' };
    };

    const novodroidUrl = novodroid_url || '';

    return (
        <div className={theme === 'light' ? "Settings" : "Settings dark"}>
            <div className="settings-cards">

                {/* Карточка 1: Telegram уведомления */}
                <div className="settings-card">
                    <div className="settings-card__header">
                        <div className="settings-card__title">
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                                <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                            </svg>
                            {t('settings:telegramNotifications')}
                        </div>
                        {telegramAccount ? (
                            <span className="settings-card__badge settings-card__badge--success">{t('settings:connected')}</span>
                        ) : (
                            <span className="settings-card__badge settings-card__badge--warning">{t('settings:notConnected')}</span>
                        )}
                    </div>
                    <p className="settings-card__desc">
                        {t('settings:notificationsDesc')}
                    </p>

                    {telegramAccount && (
                        <div className="settings-card__telegram-account">
                            <div className="settings-card__telegram-account-title">
                                {t('settings:connectedTelegramAccount')}
                            </div>
                            <div className="settings-card__telegram-account-details">
                                {telegramName && (
                                    <span>{t('settings:telegramName')}: <b>{telegramName}</b></span>
                                )}
                                {telegramAccount.username && (
                                    <span>{t('settings:telegramUsername')}: <b>@{telegramAccount.username}</b></span>
                                )}
                                {telegramAccount.id && (
                                    <span>{t('settings:telegramId')}: <b>{telegramAccount.id}</b></span>
                                )}
                            </div>
                        </div>
                    )}

                    {bot_notifier ? (
                        <>
                            <ol className="settings-card__steps">
                                <li className="settings-card__step">{t('settings:step1Notifier')}</li>
                                <li className="settings-card__step">{t('settings:step2Notifier')}</li>
                                <li className="settings-card__step">{t('settings:step3Notifier')}</li>
                            </ol>
                            <div className="settings-card__url-box">
                                <div className="settings-card__url">{notifierLink}</div>
                                <CopyButton text={notifierLink} />
                            </div>
                            <div className="settings-card__actions">
                                <a
                                    href={notifierLink}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="settings-card__btn settings-card__btn--primary"
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                                        <polyline points="15 3 21 3 21 9"/>
                                        <line x1="10" y1="14" x2="21" y2="3"/>
                                    </svg>
                                    {t('settings:goToBot')}
                                </a>
                            </div>
                        </>
                    ) : (
                        <div className="settings-card__placeholder">
                            {bot_notifier_error || t('settings:botNotConfigured')}
                        </div>
                    )}
                </div>

                {userRole === 'operator' && (
                    <div className="settings-card">
                        <div className="settings-card__header">
                            <div className="settings-card__title">
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M22 2L11 13"/><path d="M22 2l-7 20-4-9-9-4 20-7z"/>
                                </svg>
                                {t('settings:receiptChannelTitle')}
                            </div>
                            <span className={`settings-card__badge settings-card__badge--${receiptSettings?.enabled ? 'success' : 'warning'}`}>
                                {receiptSettings?.enabled ? t('settings:connected') : t('settings:notConnected')}
                            </span>
                        </div>
                        <p className="settings-card__desc">{t('settings:receiptChannelDesc')}</p>

                        <div className="settings-card__form">
                            <label className="settings-card__label">
                                {t('settings:receiptBotToken')}
                                <input
                                    className={`settings-card__input ${receiptMessage?.field === 'bot_token' ? 'settings-card__input--error' : ''}`}
                                    type="password"
                                    autoComplete="new-password"
                                    value={receiptBotToken}
                                    onChange={e => {
                                        setReceiptBotToken(e.target.value);
                                        if (receiptMessage?.field === 'bot_token') setReceiptMessage(null);
                                    }}
                                    placeholder={receiptSettings?.bot_token_configured
                                        ? t('settings:receiptTokenConfigured')
                                        : '123456:ABC...'}
                                />
                            </label>
                            <label className="settings-card__label">
                                {t('settings:receiptChannelId')}
                                <input
                                    className={`settings-card__input ${receiptMessage?.field === 'channel_id' ? 'settings-card__input--error' : ''}`}
                                    value={receiptChannelId}
                                    onChange={e => {
                                        setReceiptChannelId(e.target.value);
                                        if (receiptMessage?.field === 'channel_id') setReceiptMessage(null);
                                    }}
                                    placeholder="-1001234567890"
                                />
                            </label>
                            {receiptGroups.length > 0 && (
                                <label className="settings-card__label">
                                    {t('settings:receiptDetectedGroups')}
                                    <select
                                        className="settings-card__input"
                                        value={receiptChannelId}
                                        onChange={e => setReceiptChannelId(e.target.value)}
                                    >
                                        <option value="">{t('settings:receiptSelectGroup')}</option>
                                        {receiptGroups.map(group => (
                                            <option key={group.chat_id} value={group.chat_id}>
                                                {group.title || group.chat_id} ({group.chat_id})
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            )}
                            <label className="settings-card__checkbox">
                                <input
                                    type="checkbox"
                                    checked={receiptPdfToJpg}
                                    onChange={e => setReceiptPdfToJpg(e.target.checked)}
                                />
                                <span>
                                    <strong>{t('settings:receiptPdfToJpg')}</strong>
                                    <small>{t('settings:receiptPdfToJpgHint')}</small>
                                </span>
                            </label>
                        </div>

                        {receiptMessage && (
                            <div className={`settings-card__notice settings-card__notice--${receiptMessage.type}`}>
                                <div>{receiptMessage.text}</div>
                                {receiptMessage.hint && (
                                    <div className="settings-card__notice-hint">{receiptMessage.hint}</div>
                                )}
                            </div>
                        )}

                        <div className="settings-card__actions">
                            <button className="settings-card__btn settings-card__btn--primary" onClick={saveReceiptSettings} disabled={!!receiptBusy}>
                                {receiptBusy === 'save' ? t('settings:receiptSaving') : t('settings:receiptSave')}
                            </button>
                            <button className="settings-card__btn settings-card__btn--success" onClick={checkReceiptSettings} disabled={!!receiptBusy}>
                                {receiptBusy === 'check' ? t('settings:receiptChecking') : t('settings:receiptCheck')}
                            </button>
                            <button className="settings-card__btn settings-card__btn--warning" onClick={detectReceiptGroups} disabled={!!receiptBusy}>
                                {receiptBusy === 'detect' ? t('settings:receiptDetecting') : t('settings:receiptDetect')}
                            </button>
                        </div>
                    </div>
                )}

                {/* Карточка 2: Бот поддержки (with_token) */}
                {support_bot_mode === 'with_token' && (
                    <div className="settings-card">
                        <div className="settings-card__header">
                            <div className="settings-card__title">
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                                </svg>
                                {t('settings:supportBot')}
                            </div>
                            <span className={`settings-card__badge settings-card__badge--${getSupportStatus().type}`}>
                                {getSupportStatus().label}
                            </span>
                        </div>
                        <p className="settings-card__desc">
                            {t('settings:supportBotDesc')}
                        </p>

                        {supportLink ? (
                            <>
                                <ol className="settings-card__steps">
                                    <li className="settings-card__step">{t('settings:step1SupportToken')}</li>
                                    <li className="settings-card__step">{t('settings:step2SupportToken')}</li>
                                    <li className="settings-card__step">{t('settings:step3SupportToken')}</li>
                                </ol>
                                <div className="settings-card__url-box">
                                    <div className="settings-card__url">{supportLink}</div>
                                    <CopyButton text={supportLink} />
                                </div>
                                <div className="settings-card__actions">
                                    <a
                                        href={supportLink}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="settings-card__btn settings-card__btn--primary"
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                                            <polyline points="15 3 21 3 21 9"/>
                                            <line x1="10" y1="14" x2="21" y2="3"/>
                                        </svg>
                                        {t('settings:goToBot')}
                                    </a>
                                </div>
                            </>
                        ) : (
                            <div className="settings-card__placeholder">
                                {t('settings:supportBotNotConfigured')}
                            </div>
                        )}
                    </div>
                )}

                {/* Карточка 2 alt: Бот поддержки (without_token — просто ссылка) */}
                {support_bot_mode === 'without_token' && (
                    <div className="settings-card">
                        <div className="settings-card__header">
                            <div className="settings-card__title">
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                                </svg>
                                {t('settings:supportBot')}
                            </div>
                        </div>
                        <p className="settings-card__desc">
                            {t('settings:supportBotDesc')}
                        </p>

                        {supportLink ? (
                            <>
                                <ol className="settings-card__steps">
                                    <li className="settings-card__step">{t('settings:step1SupportNoToken')}</li>
                                    <li className="settings-card__step">{t('settings:step2SupportNoToken')}</li>
                                    <li className="settings-card__step">{t('settings:step3SupportNoToken')}</li>
                                </ol>
                                <div className="settings-card__url-box">
                                    <div className="settings-card__url">{supportLink}</div>
                                    <CopyButton text={supportLink} />
                                </div>
                                <div className="settings-card__actions">
                                    <a
                                        href={supportLink}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="settings-card__btn settings-card__btn--primary"
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                                            <polyline points="15 3 21 3 21 9"/>
                                            <line x1="10" y1="14" x2="21" y2="3"/>
                                        </svg>
                                        {t('settings:goToBot')}
                                    </a>
                                </div>
                            </>
                        ) : (
                            <div className="settings-card__placeholder">
                                {t('settings:supportBotNotConfigured')}
                            </div>
                        )}
                    </div>
                )}

                {/* Карточка 3: TotalNotify */}
                {novodroid_token && (
                    <div className="settings-card">
                        <div className="settings-card__header">
                            <div className="settings-card__title">
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
                                    <line x1="12" y1="18" x2="12.01" y2="18"/>
                                </svg>
                                {t('settings:totalNotifyTitle')}
                            </div>
                        </div>
                        <p className="settings-card__desc">
                            {t('settings:totalNotifyDesc')}
                        </p>

                        <ol className="settings-card__steps">
                            <li className="settings-card__step">
                                <span>{t('settings:downloadTotalNotify')}</span>
                                {apkAvailable && (
                                    <button
                                        className="settings-card__inline-btn"
                                        onClick={() => {
                                            const jwtToken = localStorage.getItem('accessToken');
                                            fetch(api_url + 'apk/download/', {
                                                headers: { 'Authorization': `JWT ${jwtToken}` }
                                            })
                                            .then(r => r.blob())
                                            .then(blob => {
                                                const url = window.URL.createObjectURL(blob);
                                                const a = document.createElement('a');
                                                a.href = url;
                                                a.download = 'totalnotify.apk';
                                                a.click();
                                                window.URL.revokeObjectURL(url);
                                            });
                                        }}
                                    >
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                            <polyline points="7 10 12 15 17 10"/>
                                            <line x1="12" y1="15" x2="12" y2="3"/>
                                        </svg>
                                        {t('settings:download')}
                                    </button>
                                )}
                            </li>
                            <li className="settings-card__step">{t('settings:openAppSettings')}</li>
                            <li className="settings-card__step">{t('settings:pasteUrl')}</li>
                            <li className="settings-card__step">{t('settings:appAutoProcess')}</li>
                        </ol>

                        <div className="settings-card__url-box">
                            <div className="settings-card__url">{novodroidUrl}</div>
                            <CopyButton text={novodroidUrl} />
                        </div>

                        <div className="settings-card__actions">
                            {apkAvailable ? (
                                <button
                                    className="settings-card__btn settings-card__btn--success"
                                    onClick={() => {
                                        const jwtToken = localStorage.getItem('accessToken');
                                        fetch(api_url + 'apk/download/', {
                                            headers: { 'Authorization': `JWT ${jwtToken}` }
                                        })
                                        .then(r => r.blob())
                                        .then(blob => {
                                            const url = window.URL.createObjectURL(blob);
                                            const a = document.createElement('a');
                                            a.href = url;
                                            a.download = 'totalnotify.apk';
                                            a.click();
                                            window.URL.revokeObjectURL(url);
                                        });
                                    }}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                        <polyline points="7 10 12 15 17 10"/>
                                        <line x1="12" y1="15" x2="12" y2="3"/>
                                    </svg>
                                    {t('settings:downloadApp')}
                                </button>
                            ) : (
                                <button className="settings-card__btn settings-card__btn--disabled" disabled>
                                    {t('settings:appUnavailable')}
                                </button>
                            )}
                            <button
                                className="settings-card__btn settings-card__btn--warning"
                                onClick={() => setShowRegenerateModal(true)}
                                disabled={regenerating}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="23 4 23 10 17 10"/>
                                    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                                </svg>
                                {regenerating ? t('settings:generating') : t('settings:regenerateToken')}
                            </button>
                        </div>

                        {/* Модалка подтверждения перегенерации токена */}
                        <Modal
                            isOpen={showRegenerateModal}
                            onRequestClose={() => setShowRegenerateModal(false)}
                            contentLabel={t('settings:regenerateConfirmTitle')}
                            className="confirmation-modal"
                            overlayClassName="confirmation-modal-overlay"
                        >
                            <div className="confirmation-modal-header">
                                <h2 className="confirmation-modal-title">{t('settings:regenerateTitle')}</h2>
                                <button className="confirmation-modal-close" onClick={() => setShowRegenerateModal(false)}>×</button>
                            </div>
                            <div className="confirmation-modal-content">
                                <p>{t('settings:regenerateConfirm')}</p>
                                <div className="confirmation-modal-warning">
                                    {t('settings:regenerateWarning')}
                                </div>
                            </div>
                            <div className="confirmation-modal-actions">
                                <button className="confirmation-modal-button confirm" onClick={handleRegenerateToken}>{t('common:yes')}</button>
                                <button className="confirmation-modal-button cancel" onClick={() => setShowRegenerateModal(false)}>{t('common:no')}</button>
                            </div>
                        </Modal>
                    </div>
                )}


                {/* Карточка профиля тимлидера */}
                {userRole === 'teamlead' && tlProfile && (
                    <div className="settings-card">
                        <div className="settings-card__header">
                            <div className="settings-card__title">
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
                                    <circle cx="12" cy="7" r="4"/>
                                </svg>
                                Профиль тимлидера
                            </div>
                        </div>
                        <div className="settings-card__profile-grid">
                            <div className="settings-card__profile-row">
                                <span className="settings-card__profile-label">Username</span>
                                <span className="settings-card__profile-value">{tlProfile.username} <CopyButton text={tlProfile.username} /></span>
                            </div>
                            {tlProfile.wallet && (
                                <div className="settings-card__profile-row">
                                    <span className="settings-card__profile-label">Кошелёк USDT TRC-20</span>
                                    <span className="settings-card__profile-value" style={{fontFamily: 'monospace', fontSize: '12px'}}>
                                        {tlProfile.wallet} <CopyButton text={tlProfile.wallet} />
                                    </span>
                                </div>
                            )}
                            <div className="settings-card__profile-row">
                                <span className="settings-card__profile-label">Имя</span>
                                <span className="settings-card__profile-value">{tlProfile.first_name || '—'} {tlProfile.last_name || ''}</span>
                            </div>
                            <div className="settings-card__profile-row">
                                <span className="settings-card__profile-label">Email</span>
                                <span className="settings-card__profile-value">{tlProfile.email || '—'}</span>
                            </div>
                            <div className="settings-card__profile-row">
                                <span className="settings-card__profile-label">Телефон</span>
                                <span className="settings-card__profile-value">{tlProfile.phone || '—'}</span>
                            </div>
                            <div className="settings-card__profile-row">
                                <span className="settings-card__profile-label">Баланс</span>
                                <span className="settings-card__profile-value" style={{fontWeight: 600}}>{tlProfile.balance_usdt} $</span>
                            </div>
                            <div className="settings-card__profile-row">
                                <span className="settings-card__profile-label">Стоимость трейдера</span>
                                <span className="settings-card__profile-value">{tlProfile.trader_deposit_usdt} $</span>
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </div>
    )
}
